import { LineItemTypes } from '../constants/index.js';

/**
 * Decides whether a traveler can move from one line item to another without
 * losing its step history. Product line items never take travelers (they have
 * no steps to map onto), so either side carrying `type: 'product'` is rejected
 * up front rather than relying on the missing `steps` array.
 *
 * @param {object} params
 * @param {{ projectId: string, lineItemId: string, steps: object[], type?: string }} params.from
 * @param {{ projectId: string, lineItemId: string, steps: object[], type?: string }} params.target
 * @param {number | null} [params.currentStepIndex]
 * @returns {boolean}
 */
function checkIfAutoTransferable({ from, target, currentStepIndex = null }) {
  if (from?.type === LineItemTypes.PRODUCT || target?.type === LineItemTypes.PRODUCT) {
    return false;
  }

  // Validate input objects
  if (
    !from || !target
    || !Array.isArray(from.steps) || !Array.isArray(target.steps)
    || typeof from.projectId !== 'string'
    || typeof target.projectId !== 'string'
    || typeof from.lineItemId !== 'string'
    || typeof target.lineItemId !== 'string'
  ) {
    // eslint-disable-next-line no-console
    console.error('Invalid input for checkIfAutoTransferable');
    return false;
  }

  // Projects must match
  if (from?.projectId !== target?.projectId) {
    return false;
  }

  // no need to transfer if they are the same lineItem Id
  if (from.lineItemId === target.lineItemId) {
    return false;
  }

  // Check if they are on the same tree
  if (from.steps[0]?.pPartId !== target.steps[0]?.pPartId) {
    return false;
  }

  // currentStepIndex is optional, if not exists and on the same tree, we allow transfer
  if (currentStepIndex === null || currentStepIndex <= 1) return true;

  // For currentStepIndex > 1: make sure steps before currentStepIndex are the same
  const isAutoTransferable = from.steps.every((step, index) => {
    if (index >= currentStepIndex) return true; // skip steps after or at current step
    const targetStep = target.steps[index];
    return targetStep && step.pPartId === targetStep.pPartId;
  });

  return isAutoTransferable;
}

function firebaseToProxy(firebaseUrl) {
  if (!firebaseUrl || typeof firebaseUrl !== 'string') throw new Error('Invalid Firebase URL');
  // Encode the entire Firebase URL as a query parameter for the proxy server
  const encoded = encodeURIComponent(firebaseUrl);
  // we only have one proxy server for both staging and prod, so we can hardcode the domain here
  return `https://assets.parallelfluidics.com?u=${encoded}`;
}

/**
 * Converts common persisted date shapes into a JavaScript Date.
 * Accepts Firestore Timestamp-like objects, Date instances, date strings, and millisecond numbers.
 * Returns null for missing, empty, or invalid values.
 *
 * @param {import('firebase/firestore').Timestamp | Date | string | number | null | undefined} value
 * @returns {Date | null}
 */
function normalizeDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return normalizeDate(value.toDate());

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Email domains that belong to Parallel Fluidics staff. */
const PARALLEL_STAFF_EMAIL_DOMAINS = ['@parallelfluidics.com', '@parallel.design'];

/**
 * Whether an email address is on a staff domain. Classification only (e.g.
 * which user model to use) — for access checks use {@link isParallelStaff},
 * which also requires the address to be verified.
 *
 * @param {string | null | undefined} email
 * @returns {boolean}
 */
function isParallelStaffEmail(email) {
  if (!email) return false;
  const normalized = email.toLowerCase();
  return PARALLEL_STAFF_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain));
}

/**
 * Whether a signed-in user gets staff access, e.g. previewing pending quotes.
 * Requires a verified email: anyone can create an unverified password account
 * under a staff domain. Fields are explicit because the shapes differ — web
 * `User.emailVerified` vs a callable's decoded token `email_verified`.
 *
 * @param {{ email?: string | null, emailVerified?: boolean } | null | undefined} user
 * @returns {boolean}
 */
function isParallelStaff(user) {
  return Boolean(user?.emailVerified) && isParallelStaffEmail(user?.email);
}

export {
  checkIfAutoTransferable,
  firebaseToProxy,
  isParallelStaff,
  isParallelStaffEmail,
  normalizeDate,
};
